import { BackendPlan } from './plans';
const {
    VITE_DEFAULT_X_IBM_CLIENT_ID,
    VITE_DEV_X_IBM_CLIENT_ID,
    VITE_BCBSNC_URL,
    VITE_API_URL,
    VITE_DEV_API_URL,
    VITE_AWS_URL,
    VITE_AGENT_URL,
} = import.meta.env;

export const individualRatesApi = `${VITE_API_URL}/members/v1/api/under65/individualrates`;
export const planActionApi = `${VITE_BCBSNC_URL}/sapps/buyonline/application/OnlineApplicationControl_startNew.action`;
export const planProducerActionApi = `${VITE_BCBSNC_URL}/sapps/buyonline/application/AgentAuthControl.action`;
export const emailApi = `/sapps/ratequote/mobile/rateQuote.do`;
export const individualRatesDetailsApi = `${VITE_DEV_API_URL}/members/v1/api/under65/individualrates`;
export const retrieveNewQuotesApi = `${VITE_AGENT_URL}/sapps/ratequote/prospect/RetrieveNewQuote.do`;
export const applyNowApi = `${VITE_AGENT_URL}/sapps/ratequote/producer/applyNow.do`;
export const saveQuoteApi = `${VITE_AGENT_URL}/sapps/ratequote/producer/saveQuote.do`;
export const retrieveEmailQuotApi = `${VITE_BCBSNC_URL}/sapps/shopperservices/guidedselling/RetrieveQuoteJson.do`;
export const AGENT_PORTAL_DASH_URL = () => `${VITE_AGENT_URL}/producers/secure/individual/index.htm`;
export const ibmClientId = VITE_DEFAULT_X_IBM_CLIENT_ID;
export const ibmClientDetailsId = VITE_DEV_X_IBM_CLIENT_ID;
export const publicAssetsUrl = `${VITE_AWS_URL}/get-a-quote`; // TODO: Change this to the WCMS url
export const imageUrl = `${publicAssetsUrl}/images`;

export interface Applicant {
    index: string;
    dob: string;
    firstName?: string;
    lastName?: string;
    age?: number;
    type: string;
    gender?: string;
    email?: string;
    spouse?: Applicant;
    dependents?: Applicant[];
}

export enum ApplicantRole {
    Primary = 'Primary',
    Spouse = 'Spouse',
    Child = 'Child',
}

export interface Subsidy {
    houseHoldIncome: string;
    familySize: string;
    subsidyEligible: boolean;
    csrLevel: number;
    subsidyAmt: string;
}

export interface Producer {
    producerNumber: string;
    individualAgencyId?: string;
    individualAgencyName?: string;
    groupAgencyId?: string;
    groupAgencyName?: string;
    workPhoneNumber?: string;
    internalExternal?: string;
    userID?: string;
    agencyId?: string;
    ruuid?: string;
    // found from example rateObj
    email?: string;
    firstName?: string;
    middleInitial?: string;
    lastName?: string;
    agencyName?: string;
    phone?: string;
    npn?: string;
    agencyNpn?: string;
    agentName?: string;
    agentUserId?: string;
}

export interface BackendFormResponse {
    quoteId?: string;
    referralSource: string;
    context: string;
    todaysTime: string;
    todaysDate: string;
    nextEffectiveDate: string;
    effectiveStart: string;
    coverageYear?: string;
    effectiveEnd: string;
    quoteDate: string;
    zipCode: string;
    county: string;
    regionCode: string;
    hasDental: boolean;
    hasMaternity: boolean;
    csrPercent?: string;
    applicants: Applicant[];
    plans: BackendPlan[];
    subsidy?: Subsidy;
    renewal?: boolean;
    producer?: Producer;
    enrollPeriod?: 'AEP' | 'SEP' | 'aep' | 'sep';
    currentPlan?: string;
    invokeAltQuoteAtSubsidyPage?: boolean;
    plansSeverity?: 'minimal' | 'moderate' | 'significant';
    readOnly?: boolean;
}

export interface EmailData {
    applicantEMail: string;
    applicantBirthdate: string;
    applicantZipCode: string;
    applicantCountyCode: string;
    applicantGender: 'Male' | 'Female';
    maternityCoverageIncluded: 'Y' | 'N';
    includeDental: 'Y' | 'N';
    spouseIncluded: 'true' | 'false';
    spouseBirthdate?: string;
    dependentsIncluded: 'true' | 'false';
    numberDependents: number;
    dependent?: {
        index: string;
        dob: string;
        gender: 'Male' | 'Female';
    }[];
    houseHoldIncome?: string;
    numberOfPeopleInHouseHold?: string;
    quoteFor2013: 'true' | 'false';
    quoteDate?: string;
}

export function transformEmailData(data: BackendFormResponse): EmailData {
    const primary = data.applicants[0];
    if (!primary.email) {
        throw new Error('No email found for applicant');
    }
    const spouse =
        primary.spouse ||
        data.applicants.find((applicant) => applicant.type.toLowerCase() === ApplicantRole.Spouse.toLowerCase());
    const dependents =
        primary.dependents ||
        data.applicants.filter((applicant) => applicant.type.toLowerCase() === ApplicantRole.Child.toLowerCase());

    return {
        applicantEMail: primary.email,
        applicantBirthdate: primary.dob || '',
        applicantZipCode: data.zipCode,
        applicantCountyCode: data.county,
        applicantGender: primary.gender?.toLowerCase() === 'm' ? 'Male' : 'Female',
        maternityCoverageIncluded: data.hasMaternity ? 'Y' : 'N',
        includeDental: data.hasDental ? 'Y' : 'N',
        spouseIncluded: spouse ? 'true' : 'false',
        spouseBirthdate: spouse?.dob,
        dependentsIncluded: dependents.length > 0 ? 'true' : 'false',
        numberDependents: dependents.length,
        dependent: dependents.map(({ index, dob, gender }) => ({
            index: index,
            dob: dob,
            gender: gender?.toLowerCase() === 'm' ? 'Male' : 'Female',
        })),
        houseHoldIncome: data.subsidy?.houseHoldIncome,
        numberOfPeopleInHouseHold: data.subsidy?.familySize,
        quoteFor2013: 'false',
        quoteDate: data.quoteDate,
    };
}

export interface BlueAdvantageQuoteRequest {
    processNow: boolean;
    productTypeName: string;
    enrollPeriod: string;
    deliveryMethod: string;
    applicantFirstName: string;
    applicantLastName: string;
    applicantBirthdate: string;
    applicantZipCode: string;
    countyName: string;
    applicantCountyCode: number | null;
    applicantEMail: string;
    applicantPhone: string;
    spouseIncluded: boolean;
    spouseFirstName?: string;
    spouseLastName?: string;
    spouseBirthdate?: string;
    dependentsIncluded: boolean;
    numberDependents: number;
    dependents: { dob: string }[];
    householdIncome: string;
    noOfPeopleInHouseHold?: number;
}

export async function submitBlueAdvantageQuoteRequest(request: BlueAdvantageQuoteRequest): Promise<Response> {
    const params = new URLSearchParams();
    params.append('processNow', String(request.processNow));
    params.append('productTypeName', request.productTypeName);
    params.append('enrollPeriod', request.enrollPeriod);
    params.append('deliveryMethod', request.deliveryMethod);
    params.append('applicantFirstName', request.applicantFirstName);
    params.append('applicantLastName', request.applicantLastName);
    params.append('applicantBirthdate', request.applicantBirthdate);
    params.append('applicantZipCode', request.applicantZipCode);
    params.append('countyName', request.countyName);
    params.append('applicantCountyCode', String(request.applicantCountyCode ?? ''));
    params.append('applicantEMail', request.applicantEMail);
    params.append('applicantPhone', request.applicantPhone);
    params.append('spouseIncluded', String(request.spouseIncluded));

    if (request.spouseIncluded) {
        params.append('spouseFirstName', request.spouseFirstName ?? '');
        params.append('spouseLastName', request.spouseLastName ?? '');
        params.append('spouseBirthdate', request.spouseBirthdate ?? '');
    }

    params.append('dependentsIncluded', String(request.dependentsIncluded));
    params.append('numberDependents', String(request.numberDependents));

    request.dependents.forEach((dep, i) => {
        params.append(`dependent[${i}].dob`, dep.dob);
    });

    params.append('householdIncome', request.householdIncome);
    const householdPeopleCount = request.noOfPeopleInHouseHold ?? 0;
    params.append('noOfPeopleInHouseHold', householdPeopleCount > 0 ? String(householdPeopleCount) : '');

    return fetch(`${VITE_AGENT_URL}/sapps/ratequote/producer/viewBlueAdvantageQuoteResults.do`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        credentials: 'include',
        body: params,
    });
}

/** Format a date string from YYYY-MM-DD to MM/DD/YYYY */
export function toApiDateFormat(isoDate: string): string {
    const match = isoDate.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!match) return isoDate;
    return `${match[2]}/${match[3]}/${match[1]}`;
}

/** Convert delivery method from local format to legacy format */
export function convertDeliveryMethod(deliveryMethod: 'pabol' | 'email' | ''): string {
    switch (deliveryMethod) {
        case 'pabol':
            return 'Producer Entered';
        case 'email':
            return 'Quoted Sale';
        default:
            return '';
    }
}
// Fallback for the standalone SPA (no legacy saveQuoteForm in the DOM) - mirrors the legacy form's empty POST.
export async function submitSaveQuote(): Promise<Response> {
    return fetch(saveQuoteApi, {
        method: 'POST',
        credentials: 'include',
    });
}

export async function submitBlueAdvantageSelection(plans: BackendPlan[]): Promise<Response> {
    const recommendedPlan = plans.find((p) => p.producerRecommended);
    const selection = recommendedPlan?.planId || '1';

    const params = new URLSearchParams({ selection });

    // Add all health plans with selected = false
    // Dynamically based on number of available plans
    for (let i = 0; i < plans.length; i++) {
        params.append(`healthPlan[${i}].selected`, 'false');
    }

    const response = await fetch(`${VITE_AGENT_URL}/sapps/ratequote/producer/blueAdvantageSelectPlans.do`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
        },
        credentials: 'include',
        body: params.toString(),
    });

    return response;
}

export interface ProducerEmailNotice {
    ccAgent: boolean;
    message: string;
}

export interface ProducerEmailResult {
    success: boolean;
    errorMessage?: string;
}

export async function sendProducerEmailNotice(
    inputResponse: BackendFormResponse,
    notice: ProducerEmailNotice,
    county: string
): Promise<ProducerEmailResult> {
    try {
        const recommendedPlanId = inputResponse?.plans?.find((plan) => plan.producerRecommended)?.planId || '';

        const payload = new URLSearchParams({
            dentalO65: 'false',
            dentalIncluded: 'false',
            medicareAdvantage: 'false',
            part_d: 'false',
            planYear: '',
            county: county,
            selection: recommendedPlanId,
            bodyComments: notice.message || '',
            agentEmailSelection: notice.ccAgent ? 'Y' : 'N',
        });

        const res = await fetch(`${VITE_AGENT_URL}/sapps/ratequote/producer/processRemoteRateQuote.do`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
            },
            body: payload.toString(),
            credentials: 'include',
        });

        const text = await res.text();
        const hasError = !res.ok || /error/i.test(text);

        if (hasError) {
            return {
                success: false,
                errorMessage: 'There was a problem sending your email. Please try again.',
            };
        }

        return { success: true };
    } catch (error) {
        return {
            success: false,
            errorMessage: 'There was a problem sending your email. Please try again.',
        };
    }
}
